import {
  ShiftManagementAreasViewPageDocument,
  ShiftManagementMainPageDocument,
  ShiftManagementScheduleApplyTemplateDocument,
  ShiftManagementScheduleApplyTemplateFetchDataDocument,
} from 'generated/resources/graphql'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { dateUIToBackend } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { useRefreshShiftsRows } from 'modules/shift_management/hooks/refreshShiftsRows'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { useUI } from 'modules/shift_management/hooks/useUI'
import buildEmployeeIds from 'modules/shift_management/lib/buildEmployeeIds'
import { trackAddShiftSubmitEvent } from 'modules/shift_management/lib/trackEvents'
import RestDay from 'modules/shift_management/store/internal/models/RestDay'
import Shift from 'modules/shift_management/store/internal/models/Shift'
import type ShiftsRow from 'modules/shift_management/store/internal/models/ShiftsRow'

import { type Props as ReplaceShiftsAttributes } from './useReplaceShifts'

interface Props {
  params: ReplaceShiftsAttributes['params']
  from: 'modal'
  onSuccess: () => void
}

const useApplyTemplate = ({ params, from, onSuccess }: Props) => {
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const [{ view }, dispatchSchedule] = useSchedule()
  const [, dispatchUI] = useUI()
  const { refreshShiftsRows } = useRefreshShiftsRows()

  const startDateParam = dateUIToBackend(params.startDate)
  const endDateParam = dateUIToBackend(params.endDate)
  const selectedTemplateId = params.selectedTemplateId
  const employeeIds = params.employeeIds
  const weekPosition = params.selectedWeek || 0
  const notes = params.notes
  const locationId = params.locationId
  const workAreaId = params.workAreaId

  const [applyTemplate] = useMutation(ShiftManagementScheduleApplyTemplateDocument, {
    extractorFn: (data) => data.shiftManagementMutations.applyTemplateShiftmanagementSchedule,
  })

  const [executeFetchQuery] = useLazyQuery(ShiftManagementScheduleApplyTemplateFetchDataDocument)

  const { running: submittingApply, performAction } = useAsyncAction(
    async () => {
      const mutationResult = await applyTemplate({
        variables: {
          viewStartDate: view.startAt,
          viewEndDate: view.endAt,
          templateId: selectedTemplateId,
          templateWeekPosition: Number(weekPosition),
          employeesIds: employeeIds,
          startDate: startDateParam,
          endDate: endDateParam,
          notes,
          locationId,
          workAreaId,
        },
        // TODO: Actually update the cache properly
        refetchQueries: [ShiftManagementMainPageDocument, ShiftManagementAreasViewPageDocument],
      })

      const mutationHandled = handleResult(mutationResult, (_errors) => {})
      if (mutationHandled.success) {
        const { shiftIds, restDayIds } = mutationHandled.data?.schedule || {}

        const { data: fetchData } = await executeFetchQuery({
          query: ShiftManagementScheduleApplyTemplateFetchDataDocument,
          variables: {
            shiftIds: shiftIds || [],
            restDayIds: restDayIds || [],
          },
        })

        const { shifts: newShifts, restDays: newRestDays } = fetchData?.shiftManagement || {}
        const shifts = (newShifts || []).map((shift) =>
          Shift.buildFromGraphQL({
            shift,
            employeeFullName: shift?.employee?.fullName || '',
            employeeAvatar: shift?.employee?.avatar?.url || '',
            employeeLocationId: shift?.employee?.locationId || 0,
          })
        )
        dispatchSchedule({ type: 'ADD_SHIFTS', shifts })
        dispatchShiftsRows({ type: 'ADD_SHIFTS', shifts, view })

        const restDays = (newRestDays || []).map((restDay) =>
          RestDay.build({
            id: restDay.id,
            employee_id: restDay.employee?.id || 0,
            date: restDay.date,
            state: restDay.state,
            timezone: restDay.timezone || '',
            employeeFullName: restDay.employee?.fullName || '',
            employeeAvatar: restDay.employee?.avatar?.url || '',
          })
        )
        dispatchSchedule({ type: 'ADD_REST_DAYS', restDays })
        dispatchShiftsRows({ type: 'ADD_REST_DAYS', restDays })
        dispatchUI({ type: 'REFRESHING_STATS' })

        const shiftColors = shifts.map((shift: Shift) => shift.color)
        trackAddShiftSubmitEvent({
          view,
          employeeIds,
          shiftsCount: shifts.length,
          restDaysCount: restDays.length,
          shiftType: 'template',
          color: shiftColors,
          from,
          withSearch: '',
          advancedOptions: false,
          extras: {
            template_id: selectedTemplateId,
            starting_template_week: weekPosition + 1, // for readability so first week is 1
          },
        })

        const { employeeIds: newEmployeeIds } = buildEmployeeIds(view, shiftsRows, shifts)

        const previousShiftsRows = shiftsRows.filter((row: ShiftsRow) =>
          employeeIds.includes(row.id)
        )

        await refreshShiftsRows(previousShiftsRows, newEmployeeIds)

        onSuccess()
      }
    },
    {
      loadingMessage: i18n.t('shift_management.notices.adding_shifts_notification.description'),
      successMessage: i18n.t('shift_management.actions.apply_rotating_shift_success'),
    }
  )

  return {
    onApplyTemplate: performAction,
    submittingApply,
  }
}

export default useApplyTemplate
